/* BP-020 · KPI Intelligence Explorer.
   Catalog view + drill-in + sparkline grid. Consumes the canonical
   KPI service — the same source of truth BP-019 dashboard uses.  */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Brain, Sparkles,
  Activity, TrendingUp, TrendingDown, Database, ArrowRight,
  ChevronDown, ChevronRight, Search,
} from "lucide-react";
import DealScoreSparkline from "@/components/v3/DealScoreSparkline";
import CallFunnelTile from "@/pages/v3/kpi/CallFunnelTile";


const BAND_COLORS = {
  elite:    { text: "#065f46", bg: "#a7f3d0", label: "Elite" },
  strong:   { text: "#047857", bg: "#d1fae5", label: "Strong" },
  healthy:  { text: "#b45309", bg: "#fef3c7", label: "Healthy" },
  watch:    { text: "#c2410c", bg: "#ffedd5", label: "Watch" },
  critical: { text: "#b91c1c", bg: "#fee2e2", label: "Critical" },
  nodata:   { text: "#64748b", bg: "#f1f5f9", label: "Awaiting data" },
};


// Reroute "critical band + no data yet" to a friendlier "awaiting data" pill.
// Fresh workspaces would otherwise be a wall of red panic — visually distracting
// and false-positive (metric isn't critical, there's literally nothing to score).
function effectiveBand(kpi) {
  const v = kpi?.value;
  const hasData = v !== null && v !== undefined && Number(v) !== 0;
  if (!hasData && kpi?.band === "critical") return "nodata";
  return kpi?.band || "nodata";
}


// The five metrics an operator actually lives or dies by.
// Pinned above every other KPI so they never get lost in the accordion.
const BIG_5_IDS = [
  "revenue",
  "pipeline_value",
  "close_rate",
  "lead_response_time_min",
  "contracts",
];


// Friendly domain labels + one-line "what this measures" copy for accordion headers.
const DOMAIN_META = {
  financial:    { label: "💰 Financial",    blurb: "Money in, money out, cash flow." },
  revenue:      { label: "📈 Revenue",      blurb: "Pipeline value, contracts, fees." },
  marketing:    { label: "📣 Marketing",    blurb: "Spend, ROI, cost per lead." },
  acquisition:  { label: "🎯 Acquisition",  blurb: "Conversion rates through the funnel." },
  disposition:  { label: "🤝 Disposition",  blurb: "Buyer-side deal flow." },
  operational:  { label: "⚡ Operational",  blurb: "Speed metrics — response, close, cycle." },
  negotiation:  { label: "🗣️ Negotiation",  blurb: "Cycle time, counteroffers, win rate." },
  buyer:        { label: "👥 Buyer Network", blurb: "Active buyers, funding, LTV." },
  portfolio:    { label: "🏘️ Portfolio",    blurb: "Owned properties · value, equity, cash flow." },
  market:       { label: "🗺️ Market",       blurb: "Where you play — heat, momentum, fit." },
  coach:        { label: "🧠 AI Coach",     blurb: "Analyses run, adoption, evidence density." },
  platform:     { label: "🛡️ Platform",     blurb: "Cross-tenant admin (superadmin only)." },
};


function fmt(value, unit) {
  if (value === null || value === undefined) return "—";
  const n = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(n)) return "—";
  if (unit === "currency") {
    if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (Math.abs(n) >= 10_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (unit === "percent") return `${n.toFixed(1)}%`;
  if (unit === "duration_min") {
    if (n < 60) return `${Math.round(n)}m`;
    if (n < 60 * 24) return `${(n / 60).toFixed(1)}h`;
    return `${(n / 60 / 24).toFixed(1)}d`;
  }
  return n.toLocaleString();
}


function BandPill({ band, testid }) {
  const cfg = BAND_COLORS[band] || BAND_COLORS.nodata;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center px-1.5 py-0.5 rounded-full
                 text-[9px] font-bold uppercase tracking-wider ring-1"
      style={{
        color: cfg.text, background: cfg.bg, borderColor: cfg.text,
      }}
    >
      {cfg.label}
    </span>
  );
}


function DeltaPill({ value, direction = "higher_is_better" }) {
  if (value === null || value === undefined) {
    return (
      <span data-testid="kpi-delta-none"
             className="text-[10px] text-slate-400">
        baseline
      </span>
    );
  }
  // For lower-is-better metrics, a NEGATIVE delta is GOOD.
  const positiveIsGood = direction === "higher_is_better";
  const isGood = positiveIsGood ? value >= 0 : value <= 0;
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  const cls = isGood
    ? "text-emerald-700 bg-emerald-50 ring-emerald-200"
    : "text-red-700 bg-red-50 ring-red-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-1.5
                     py-0.5 text-[10px] font-medium ring-1 ${cls}`}>
      <Icon size={10} />
      {value > 0 ? "+" : ""}{value.toFixed(1)}%
    </span>
  );
}


function KPITile({ kpi, delta, series, onClick }) {
  return (
    <button
      onClick={() => onClick(kpi)}
      data-testid={`kpi-tile-${kpi.id}`}
      className="text-left rounded-xl border border-slate-200 bg-white p-3
                 hover:border-amber-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">
          {kpi.label}
        </div>
        <BandPill band={effectiveBand(kpi)} testid={`kpi-band-${kpi.id}`} />
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <div className="text-xl font-semibold text-slate-900 tabular-nums">
          {fmt(kpi.value, kpi.unit)}
        </div>
        <DeltaPill value={delta} direction={kpi.direction} />
      </div>
      <div className="mt-2 h-6">
        {series?.length >= 2 ? (
          <DealScoreSparkline
            points={series.map((p) => ({ score: p.value }))}
            width={200} height={22}
            testid={`kpi-spark-${kpi.id}`}
          />
        ) : (
          <div className="text-[10px] text-slate-400 italic">
            {series?.length === 1 ? "1 snapshot" : "no history yet"}
          </div>
        )}
      </div>
    </button>
  );
}


function KPIDrawer({ kpi, series, forecast, onClose }) {
  if (!kpi) return null;
  return (
    <div
      data-testid="kpi-drawer"
      className="fixed right-0 top-0 h-full w-[440px] bg-white shadow-2xl
                 border-l border-slate-200 z-40 overflow-y-auto"
    >
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {kpi.domain} · KPI
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {kpi.label}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="kpi-drawer-close"
            className="text-slate-400 hover:text-slate-800 text-sm"
          >
            ✕
          </button>
        </div>
        <div className="mt-3 flex items-baseline gap-3">
          <div className="text-3xl font-bold text-slate-900 tabular-nums">
            {fmt(kpi.value, kpi.unit)}
          </div>
          <BandPill band={effectiveBand(kpi)} testid="kpi-drawer-band" />
        </div>
        <div className="mt-2 text-xs text-slate-600">{kpi.explain}</div>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            History
          </div>
          {series?.length >= 2 ? (
            <DealScoreSparkline
              points={series.map((p) => ({ score: p.value }))}
              width={400} height={64}
              testid="kpi-drawer-sparkline"
            />
          ) : (
            <div className="text-xs text-slate-400 italic">
              Not enough snapshots yet — recalculate at least 2 times to
              show a trend.
            </div>
          )}
        </div>

        <div data-testid="kpi-drawer-forecast">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            Forecast · Next {forecast?.horizon_days || 30} days
          </div>
          {forecast ? (
            <>
              <div className="flex items-baseline gap-3">
                <div className="text-xl font-semibold text-slate-900 tabular-nums">
                  {fmt(forecast.projected, kpi.unit)}
                </div>
                <span className="text-[10px] uppercase tracking-wider
                                 text-slate-400 font-mono">
                  {forecast.method}
                </span>
                {forecast.method === "regression" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded
                                   bg-indigo-50 text-indigo-700 ring-1
                                   ring-indigo-100">
                    R² {forecast.r_squared?.toFixed(2)}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Confidence {(forecast.confidence * 100).toFixed(0)}%
                {forecast.method === "trailing"
                  && " · needs 3+ snapshots for regression"}
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-400">—</div>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            Targets
          </div>
          <div className="grid grid-cols-4 gap-1 text-[10px]">
            {["elite", "strong", "healthy", "watch"].map((b) => (
              <div key={b} className="rounded p-1.5 text-center"
                    style={{
                      background: BAND_COLORS[b].bg,
                      color: BAND_COLORS[b].text,
                    }}>
                <div className="uppercase tracking-wider font-bold">
                  {BAND_COLORS[b].label}
                </div>
                <div className="tabular-nums">
                  {fmt(kpi.target?.[b], kpi.unit)}
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">
            {kpi.direction === "higher_is_better"
              ? "Higher is better."
              : "Lower is better."}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function KPIExplorerPage() {
  const [dash, setDash]         = useState(null);
  const [trends, setTrends]     = useState(null);
  const [forecasts, setForecasts] = useState(null);
  const [expl, setExpl]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [selected, setSelected] = useState(null);
  const [domainFilter, setDomainFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, t, f] = await Promise.all([
        api.get(`/v3/kpi/dashboard?window_days=30`),
        api.get(`/v3/kpi/trends?window_days=90`),
        api.get(`/v3/kpi/forecast?window_days=30&horizon_days=30`),
      ]);
      setDash(d.data);
      setTrends(t.data);
      setForecasts(f.data);
      try {
        const { data } = await api.get(
          `/v3/kpi/explanations/latest?window_days=30`);
        setExpl(data);
      } catch {
        setExpl(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || "Failed to load KPI dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recalculate = useCallback(async () => {
    setRecalculating(true);
    try {
      const { data } = await api.post(
        `/v3/kpi/recalculate?window_days=30`);
      toast.success(`Wrote ${data.metrics_written} snapshots`);
      await load();
    } catch (err) {
      toast.error("Recalculate failed");
    } finally {
      setRecalculating(false);
    }
  }, [load]);

  const generateExplanations = useCallback(async () => {
    setGeneratingAI(true);
    toast.message("Generating AI explanations…", { duration: 3000 });
    try {
      const { data } = await api.post(`/v3/kpi/explanations`,
        { window_days: 30, horizon_days: 30, force: true });
      setExpl(data);
      toast.success(data.mode === "ai"
        ? "AI explanations generated"
        : `AI unavailable — deterministic fallback (${data.fallback_reason || ""})`);
    } catch (err) {
      toast.error("Failed to generate explanations");
    } finally {
      setGeneratingAI(false);
    }
  }, []);

  const kpiList = useMemo(() => {
    if (!dash) return [];
    return Object.values(dash.kpis).filter(
      (k) => domainFilter === "all" || k.domain === domainFilter);
  }, [dash, domainFilter]);

  const domains = dash?.domains || [];

  // Big 5 tiles — always visible, above accordion.
  const big5 = useMemo(() => {
    if (!dash) return [];
    return BIG_5_IDS
      .map((id) => dash.kpis?.[id])
      .filter(Boolean);
  }, [dash]);

  // KPIs grouped by domain (Big 5 excluded — they live in the pinned strip).
  const groupedByDomain = useMemo(() => {
    if (!dash) return [];
    const groups = {};
    for (const kpi of Object.values(dash.kpis)) {
      if (BIG_5_IDS.includes(kpi.id)) continue;
      if (domainFilter !== "all" && kpi.domain !== domainFilter) continue;
      (groups[kpi.domain] ||= []).push(kpi);
    }
    // Preserve catalog domain order
    return domains
      .filter((d) => groups[d]?.length)
      .map((d) => ({
        domain: d,
        meta: DOMAIN_META[d] || { label: d, blurb: "" },
        kpis: groups[d],
      }));
  }, [dash, domainFilter, domains]);

  // Which accordions are open — remembered client-side for the session.
  const [openDomains, setOpenDomains] = useState(() => new Set(["operational", "buyer"]));
  const toggleDomain = useCallback((d) => {
    setOpenDomains((prev) => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  }, []);

  // Health score — one number that summarises the whole workspace.
  const [health, setHealth] = useState(null);
  useEffect(() => {
    if (!dash) return;
    api.get("/v3/kpi/health-score?window_days=30")
      .then(({ data }) => setHealth(data))
      .catch(() => setHealth(null));
  }, [dash]);

  const healthBand =
    !health?.score ? "nodata"
    : (health.band && typeof health.band === "object" ? health.band.id : health.band)
      || (health.score >= 85 ? "elite"
        : health.score >= 70 ? "strong"
        : health.score >= 50 ? "healthy"
        : health.score >= 30 ? "watch"
        : "critical");

  const totalCount = dash ? Object.keys(dash.kpis).length : 0;
  const domainCount = domains.length;

  return (
    <div className="space-y-6 relative" data-testid="kpi-explorer-page">
      {/* ── HERO — Health Score + AI storyline + primary actions ─────── */}
      <div
        data-testid="kpi-hero"
        className="rounded-2xl border border-slate-200 bg-gradient-to-br
                   from-white via-white to-amber-50/60 p-6 shadow-sm"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          {/* Left — headline + AI storyline */}
          <div className="flex-1 min-w-[280px]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-bold">
              Your workspace at a glance
            </div>
            <h1 className="mt-1 text-3xl md:text-4xl font-black tracking-tight text-slate-900">
              KPI Command Center
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-xl">
              {totalCount} live metrics across {domainCount} domains — sourced
              from the same engine that powers your Daily Briefing and Health
              Score. Tap any tile to drill in.
            </p>

            {expl && (
              <div
                data-testid="kpi-hero-ai-storyline"
                className="mt-4 flex items-start gap-3 rounded-xl bg-white border border-indigo-200 p-3"
              >
                {expl.mode === "ai"
                  ? <Brain size={18} className="text-indigo-600 shrink-0 mt-0.5" />
                  : <Sparkles size={18} className="text-indigo-600 shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-indigo-700 font-bold">
                    AI Storyline
                    {typeof expl.confidence === "number" && (
                      <span className="ml-2 text-[10px] text-indigo-500 font-mono normal-case tracking-normal">
                        {(expl.confidence * 100).toFixed(0)}% confident
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-sm text-slate-800 leading-snug">
                    {expl.headline || "Snapshot the workspace, then generate an AI storyline."}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right — Health score ring */}
          <div
            data-testid="kpi-hero-health"
            className="rounded-2xl bg-white border border-slate-200 p-5 min-w-[240px] shadow-sm"
          >
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Executive Health Score
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-5xl font-black tabular-nums text-slate-900">
                {health?.score?.toFixed(0) ?? "—"}
              </div>
              <BandPill band={healthBand} testid="kpi-hero-health-band" />
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              6-dimension composite · 30-day window
            </div>
            {Array.isArray(health?.dimensions) && health.dimensions.length > 0 && (
              <div className="mt-3 space-y-1 text-[10px]">
                {health.dimensions.slice(0, 6).map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2">
                    <span className="text-slate-500 truncate">{d.label}</span>
                    <span className="tabular-nums font-mono text-slate-700 shrink-0">
                      {typeof d.score === "number" ? d.score.toFixed(0) : "—"}
                      <span className="text-slate-400 ml-0.5">/{d.weight ?? "—"}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action row */}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <button
            onClick={recalculate}
            disabled={recalculating}
            data-testid="kpi-recalculate-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200
                       bg-white hover:bg-slate-50 flex items-center gap-1
                       disabled:opacity-50"
          >
            {recalculating
              ? <Loader2 size={13} className="animate-spin" />
              : <Database size={13} />}
            Snapshot now
          </button>
          <button
            onClick={generateExplanations}
            disabled={generatingAI}
            data-testid="kpi-generate-ai-btn"
            className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r
                       from-indigo-500 to-purple-500 text-white
                       hover:from-indigo-600 hover:to-purple-600
                       flex items-center gap-1 shadow-sm disabled:opacity-50"
          >
            {generatingAI
              ? <Loader2 size={13} className="animate-spin" />
              : <Brain size={13} />}
            Refresh AI storyline
          </button>
          <button
            onClick={load}
            disabled={loading}
            data-testid="kpi-refresh-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200
                       bg-white hover:bg-slate-50 flex items-center gap-1
                       disabled:opacity-50"
          >
            {loading
              ? <Loader2 size={13} className="animate-spin" />
              : <RefreshCw size={13} />}
            Reload
          </button>
          {/* Domain quick-filter — kept, but demoted to the right */}
          <div className="ml-auto flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">
              Filter
            </span>
            <select
              data-testid="kpi-domain-filter"
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white capitalize"
            >
              <option value="all">All domains</option>
              {domains.map((d) => (
                <option key={d} value={d}>{DOMAIN_META[d]?.label || d}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── BIG 5 — pinned high-priority tiles ────────────────────────── */}
      {big5.length > 0 && domainFilter === "all" && (
        <div data-testid="kpi-big5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              The Big 5 — your make-or-break metrics
            </span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {big5.map((kpi) => (
              <KPITile
                key={kpi.id}
                kpi={kpi}
                delta={dash.deltas?.[kpi.id]}
                series={trends?.series?.[kpi.id]}
                onClick={setSelected}
              />
            ))}
          </div>
        </div>
      )}

      {/* Call Mode step drop-off — feeds off `deal_call_sessions`. */}
      {domainFilter === "all" && (
        <div data-testid="kpi-call-funnel-wrap">
          <div className="flex items-center gap-2 mb-2 mt-4">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Call Mode — where deals leak on the phone
            </span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
          <CallFunnelTile />
        </div>
      )}

      {/* ── LOADING ────────────────────────────────────────────────── */}
      {loading && !dash && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 grid place-items-center">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      )}

      {/* ── DOMAIN ACCORDIONS ─────────────────────────────────────── */}
      {!loading && groupedByDomain.map(({ domain, meta, kpis }) => {
        const isOpen = openDomains.has(domain);
        const counts = kpis.reduce((acc, k) => {
          const b = effectiveBand(k);
          acc[b] = (acc[b] || 0) + 1;
          return acc;
        }, {});
        return (
          <div
            key={domain}
            data-testid={`kpi-domain-accordion-${domain}`}
            className="rounded-xl border border-slate-200 bg-white overflow-hidden"
          >
            <button
              onClick={() => toggleDomain(domain)}
              data-testid={`kpi-domain-toggle-${domain}`}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition"
            >
              {isOpen
                ? <ChevronDown size={16} className="text-slate-500 shrink-0" />
                : <ChevronRight size={16} className="text-slate-500 shrink-0" />}
              <div className="flex-1 text-left">
                <div className="text-sm font-bold text-slate-900">
                  {meta.label}
                  <span className="ml-2 text-[10px] font-normal text-slate-500">
                    {kpis.length} metric{kpis.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500">{meta.blurb}</div>
              </div>
              {/* Band count summary — quick health scan without opening */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {["elite", "strong", "healthy", "watch", "critical", "nodata"].map((b) => (
                  counts[b] ? (
                    <span
                      key={b}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono tabular-nums"
                      style={{
                        color: BAND_COLORS[b].text,
                        background: BAND_COLORS[b].bg,
                      }}
                    >
                      {counts[b]}
                      <span className="uppercase tracking-wider text-[8px]">
                        {b === "nodata" ? "—" : b[0]}
                      </span>
                    </span>
                  ) : null
                ))}
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 p-4 bg-slate-50/40">
                <div
                  data-testid={`kpi-grid-${domain}`}
                  className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
                >
                  {kpis.map((kpi) => (
                    <KPITile
                      key={kpi.id}
                      kpi={kpi}
                      delta={dash.deltas?.[kpi.id]}
                      series={trends?.series?.[kpi.id]}
                      onClick={setSelected}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ── FOOTER — catalog version ───────────────────────────────── */}
      {dash?.catalog_version && (
        <div className="text-[10px] text-slate-400 font-mono">
          catalog {dash.catalog_version} · {totalCount} KPIs · {domainCount} domains ·
          sources {dash.sources_hash?.slice(0, 20)}
        </div>
      )}

      {/* Drill-in drawer */}
      {selected && (
        <KPIDrawer
          kpi={selected}
          series={trends?.series?.[selected.id]}
          forecast={forecasts?.forecasts?.[selected.id]
                     && { ...forecasts.forecasts[selected.id],
                           horizon_days: forecasts.horizon_days }}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
